
import { useTranslation } from 'react-i18next';

import { useAnswerRefHtml } from './answer-ref-context';
import { chipToneClass } from './eval-chip';
import { useReportFiredStates } from './fired-states';
import { PreviewHtml, processVariableChipHTML, useChipScope } from './variable-chip-preview';

import type { FeedbackState } from '@agavi/blocks';

import { sanitizeAuthoredHtml } from '@/lib/sanitize-authored-html';

/**
 * What a listener is told before the feedback itself.
 *
 * Feedback has a loud visual language — its own tinted box, a big handwritten tick or cross, a
 * coloured rail down the side of the block — and every bit of that was invisible to a screen reader.
 * The glyph is `aria-hidden` (correctly: it is a decorative character, and "check mark" is not a
 * sentence), the tint is CSS, and the rail belongs to a container two levels up. So what actually
 * reached a listener was the teacher's prose on its own, with nothing to mark it out from the
 * question it follows and no statement of the verdict at all. On a wrong answer whose feedback reads
 * "Remember to subtract the tare weight", a listener had no way to know they had got it wrong.
 *
 * Names the KIND and the SYMBOL, and stops there. It used to carry a verdict clause as well —
 * "Feedback, X symbol, that is not the correct answer" — and that was one statement too many: the
 * symbol already IS the verdict in this app's language, so the clause said the same thing twice and
 * did it in words that appear nowhere on screen. A sighted student gets "feedback" from the box and
 * the mark from the glyph; a listener now gets exactly those two, and then whatever the teacher
 * wrote.
 *
 * Literal `t()` calls in an exhaustive switch, never a template key: a template trips the
 * dynamic-key ceiling in `invariants.test.ts`, and a `Record` would be invisible to that ceiling AND
 * to the every-key-exists check.
 */
export function spokenFeedbackPreface(
  evaluation: FeedbackState['evaluation'],
  t: (key: string) => string,
): string {
  switch (evaluation) {
    case 'correct': return t('task.feedback.spokenCorrect');
    case 'incorrect': return t('task.feedback.spokenIncorrect');
    default: return t('task.feedback.spokenNeutral');
  }
}

/**
 * The feedback a student sees once an answer is graded — one implementation for all eleven blocks.
 *
 * There were eleven of these, each a near-verbatim port of MCQ's, and they had drifted only in
 * variable names and line breaks. That is not harmless duplication: `{firedFeedback}` was written
 * twice on consecutive lines in GPS, and because Compass and Angle are ports of GPS, all three
 * showed every student's feedback twice — in Preview and in the teacher's test widget — until it
 * was noticed by eye.
 *
 * `data-eval` is the second reason this exists. Preview's block container colours its left rail
 * from the result, and it can only do that if the result is legible from outside the block. The
 * attribute is that contract, stated once here rather than depended upon as a side effect of which
 * background class a tone happens to use.
 */
export function FiredFeedback({ states, rolledValues, className }: {
  states: FeedbackState[] | null;
  /**
   * Resolve variable chips in the feedback text against this roll.
   *
   * Optional because the blocks genuinely differ: MCQ, Dropdown, Keyword, FITB and Ordering
   * resolve chips in their feedback, while the numeric and sensor blocks render the stored HTML
   * as-is. Preserved rather than unified, because making six blocks start resolving chips is a
   * behaviour change and not this component's business — but it now differs in ONE argument
   * instead of in six copies of the same function.
   */
  rolledValues?: Record<string, string>;
  /** The sensors wrap their stack in their own spacing; every other block drops the boxes straight
   * into an existing flex column, where a wrapper would break the gap. */
  className?: string;
}) {
  const { t } = useTranslation();
  // Hooks before the early return below, which is also why this one is read here and not at
  // its use site inside `content`.
  // Feedback only exists once a block has been answered, so a roll here always means a test
  // surface — and a reference inside it resolves for the same reason one in the question does.
  const answerRefHtml = useAnswerRefHtml(rolledValues ?? null);
  const chipScope = useChipScope();
  // Before the early return, because a block that STOPS firing has to clear its entry: a stale
  // verdict left in the registry would route the next submission on the previous one's answer.
  useReportFiredStates(states);
  if (!states || states.length === 0) return null;

  const glyph = (e: FeedbackState['evaluation']) => e === 'correct' ? '✓' : e === 'incorrect' ? '✗' : '–';
  const content = (s: FeedbackState) => (rolledValues
    // Feedback is prose, so it can hold a reference like any other prose — and this branch
    // renders stored html directly, so it needs the hook that `PreviewHtml` applies below.
    ? <div key={s.id} className='ws-rich-text' dangerouslySetInnerHTML={{ __html: sanitizeAuthoredHtml(processVariableChipHTML(answerRefHtml(s.content), rolledValues, chipScope)) }} />
    : <PreviewHtml key={s.id} html={s.content} className='ws-rich-text' />);

  const box = (key: string, evaluation: FeedbackState['evaluation'], body: React.ReactNode) => (
    <div
      key={key}
      // The hook Preview's block container reads, and what the accessibility emulator reads to give
      // this row its spoken preface. CSS and scanning only — the preface below is the aria half.
      data-eval={evaluation}
      className={`flex items-center gap-3 rounded-md px-3 py-2 text-sm ${chipToneClass(evaluation)}`}
    >
      {/* The whole visual language of a feedback box, in words, for the listener who gets none of
          it. See `spokenFeedbackPreface`. Ahead of the glyph so it is the first thing heard, and
          `sr-only` rather than an `aria-label` on the box: a label would REPLACE the teacher's text
          for anything that reads the box as a whole. */}
      <span className='sr-only'>{spokenFeedbackPreface(evaluation, t)}</span>
      <span aria-hidden='true' className='font-handwritten shrink-0 text-3xl font-bold leading-none'>{glyph(evaluation)}</span>
      {body}
    </div>
  );

  // A default state is its own box. Targeted states CLUSTER by evaluation — several can fire at
  // once (one per wrong blank in FITB, one per wrong row elsewhere), and a box each would bury the
  // answer under a column of near-identical cards.
  const nodes: React.ReactNode[] = states
    .filter(s => s.type === 'default')
    .map(s => box(s.id, s.evaluation, content(s)));

  const targeted = states.filter(s => s.type === 'targeted');
  for (const evaluation of ['incorrect', 'neutral'] as const) {
    const group = targeted.filter(s => s.evaluation === evaluation);
    if (group.length === 0) continue;
    nodes.push(box(evaluation, evaluation, (
      <div className='flex min-w-0 flex-1 flex-col gap-1.5'>{group.map(content)}</div>
    )));
  }

  return className ? <div className={className}>{nodes}</div> : <>{nodes}</>;
}
